import React, { useState, useCallback, useMemo, useEffect } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { AlertTriangle } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PAGE_SIZE } from "@shared/utils/constants";
import {
  SystemAlertRecommendationFilters,
  SystemAlertRecommendationItem,
} from "../types"; // Adjust import path
import {
  useDismissAlert,
  useReviewAlert,
  useSystemAlertRecommendations,
} from "../hooks"; // Adjust import path
import { SystemAlertFilterPanel } from "./FilterPanel";

export const SystemAlertTable: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);

  // Parse filters from URL params
  const getFiltersFromUrl =
    useCallback((): SystemAlertRecommendationFilters => {
      const urlFilters: SystemAlertRecommendationFilters = {
        page: currentPage,
        limit: PAGE_SIZE,
      };

      // Parse search data
      const queryParam = searchParams.get("search");
      if (queryParam) {
        urlFilters.search = queryParam;
      }

      // Parse generateDate filter
      const generateDateParam = searchParams.get("generate_date");
      if (generateDateParam) {
        urlFilters.generateDate = generateDateParam;
      }

      // Parse status filters
      const statusParam = searchParams.get("status");
      if (statusParam && statusParam !== "all") {
        urlFilters.status = statusParam;
      }

      // Parse priority filter
      const priorityParam = searchParams.get("priority");
      if (priorityParam && priorityParam !== "all") {
        urlFilters.priority = priorityParam;
      }

      return urlFilters;
    }, [searchParams, currentPage]);

  // Memoize filters to prevent unnecessary re-computation
  const filters = useMemo(() => getFiltersFromUrl(), [getFiltersFromUrl]);

  // API hook for recommendations data
  const { data, isLoading, error } = useSystemAlertRecommendations(filters);
  const dismissAlert = useDismissAlert();
  const reviewAlert = useReviewAlert();

  // Add missing pagination calculations
  const totalItems = data?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;

  // Memoize pagination object (like SystemsTable)
  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: startIndex + 1,
      endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
    }),
    [currentPage, totalPages, totalItems, startIndex]
  );

  // Handle search with proper URL management
  const handleSearch = useCallback(
    (searchTerm: string, paramName: string) => {
      const newParams = new URLSearchParams(searchParams);

      if (searchTerm.trim()) {
        newParams.set(paramName, searchTerm.trim());
      } else {
        newParams.delete(paramName);
      }

      // Reset to page 1 when searching
      newParams.set("page", "1");
      setCurrentPage(1);

      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Handle pagination with state sync
  const handlePageChange = useCallback(
    (newPage: number) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", newPage.toString());

      setSearchParams(newParams, { replace: true });
      setCurrentPage(newPage); // Update local state
    },
    [searchParams, setSearchParams]
  );

  // Helper functions for badge colors
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "new":
        return "bg-blue-100 text-blue-800";
      case "reviewed":
        return "bg-green-100 text-green-800";
      case "in_progress":
        return "bg-yellow-100 text-yellow-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "rejected":
      case "dismissed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getConditionScoreColor = (score: string) => {
    const numScore = parseFloat(score);
    if (numScore >= 80) return "bg-green-100 text-green-800";
    if (numScore >= 60) return "bg-yellow-100 text-yellow-800";
    if (numScore >= 40) return "bg-orange-100 text-orange-800";
    return "bg-red-100 text-red-800";
  };

  const getRiskScoreColor = (score: string) => {
    const numScore = parseFloat(score);
    if (numScore >= 600) return "bg-red-100 text-red-800";
    if (numScore >= 400) return "bg-orange-100 text-orange-800";
    if (numScore >= 200) return "bg-yellow-100 text-yellow-800";
    return "bg-green-100 text-green-800";
  };

  // Table columns configuration
  const columns: TableColumn<SystemAlertRecommendationItem>[] = [
    {
      key: "assetName",
      header: "Asset ID / Name",
      type: "custom",
      render: (value: string, row: SystemAlertRecommendationItem) => (
        <div>
          <div>{row.asset.assetNo}</div>
          <div>{row.asset.assetName}</div>
        </div>
      ),
      headerTooltip: "The specific asset identified.",
    },
    {
      key: "location",
      header: "Location",
      type: "custom",
      render: (value, row: SystemAlertRecommendationItem) => (
        <span
          title={row.asset.location}
          className="inline-block max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer
               bg-transparent text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200 px-1 py-1 rounded"
        >
          {row.asset.location?.length > 15
            ? `${row.asset.location.substring(0, 15)}...`
            : row.asset.location || "N/A"}
        </span>
      ),
      headerTooltip: "The location of the asset.",
    },
    {
      key: "generateDate",
      header: "Generate Date",
      type: "text",
      sortable: true,
      headerTooltip: "The asset's current condition score.",
    },
    {
      key: "conditionScore",
      header: "Condition Score",
      type: "custom",
      render: (value: string) => (
        <Badge className={getConditionScoreColor(value)} variant="secondary">
          {value}
        </Badge>
      ),
    },
    {
      key: "riskScore",
      header: "Risk Score",
      type: "custom",
      render: (value: string) => (
        <Badge className={getRiskScoreColor(value)} variant="secondary">
          {value}
        </Badge>
      ),
      headerTooltip: "The asset's current risk score.",
    },
    {
      key: "actionDate",
      header: "Action Date",
      type: "text",
      sortable: true,
      headerTooltip: "The suggested date by which action should be taken.",
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "custom",
      render: (value: string, row: SystemAlertRecommendationItem) => (
        <Badge className={getStatusColor(value)} variant="secondary">
          {value}
        </Badge>
      ),
      headerTooltip:
        "A colored tag indicating the recommendation's state (New, Reviewed, Overdue, Action Taken, Dismissed).",
    },
    {
      key: "lastModifiedUserRemoteName",
      header: "Reviewed By",
      type: "custom",
      headerTooltip: "The name of the user who last reviewed the asset.",
    },
    {
      key: "reviewedOn",
      header: "Reviewed On",
      type: "custom",
      headerTooltip: "The date on which the review was made.",
    },
  ];

  // Table actions
  const actions: TableAction<SystemAlertRecommendationItem>[] = [
    {
      label: "View Details",
      onClick: (row: SystemAlertRecommendationItem) => {
        navigate(`/ax/asset-registry/assets/view/${row.id}`);
      },
      icon: "Eye",
      variant: "outline",
    },
    {
      label: "Mark Reviewed",
      onClick: (row: SystemAlertRecommendationItem) => {
        reviewAlert.mutate(row.id);
      },
      icon: "Check",
      variant: "outline",
    },
    {
      label: "Dismiss Recommendation",
      onClick: (row: SystemAlertRecommendationItem) => {
        dismissAlert.mutate(row.id);
      },
      icon: "X",
      variant: "outline",
    },
  ];

  // Handle apply filters
  const handleApplyFilters = useCallback(
    (formFilters: any) => {
      const newParams = new URLSearchParams(searchParams);

      // Clear ALL existing filter params
      newParams.delete("generate_date");
      newParams.delete("status");
      newParams.delete("priority");

      // Apply generateDate filter
      if (formFilters.generateDate) {
        newParams.set("generate_date", formFilters.generateDate);
      }

      // Apply status filter
      if (formFilters.status?.length && !formFilters.status.includes("all")) {
        newParams.set("status", formFilters.status);
      }

      // Apply priority filter
      if (formFilters.priority && formFilters.priority !== "all") {
        newParams.set("priority", formFilters.priority);
      }

      // Reset to page 1 when applying filters
      newParams.set("page", "1");
      setCurrentPage(1);

      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Handle reset filters
  const handleResetFilters = useCallback(() => {
    const newParams = new URLSearchParams();

    // Only keep the current search term if it exists
    const currentSearch = searchParams.get("search");
    if (currentSearch) {
      newParams.set("search", currentSearch);
    }

    // Always start from page 1
    newParams.set("page", "1");
    setCurrentPage(1);

    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Handle export
  const handleExport = () => {
    // TODO: Implement export functionality
  };

  return (
    <GenericTable
      data={data?.results}
      columns={columns}
      rowKey="id"
      searchPlaceholder="Search recommendations by asset name, ID, location..."
      searchParamName="search"
      searchDebounceMs={300}
      onSearch={handleSearch}
      onExport={handleExport}
      pagination={pagination}
      onPageChange={handlePageChange}
      actions={actions}
      actionsType="dropdown"
      filterPanel={
        <SystemAlertFilterPanel
          currentFilters={{
            generateDate: filters.generateDate,
            status: filters.status,
            priority: filters.priority,
          }}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
        />
      }
      emptyMessage="No recommendations found"
      emptyIcon={<AlertTriangle className="h-8 w-8 text-gray-400" />}
    />
  );
};
